import { makeAutoObservable, runInAction } from "mobx";

import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { surveysService } from "../services";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const resolvePayload = (payload) => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return payload;
};

const resolveSurveyType = (survey) => {
  if (!survey) return "StandardSurvey";
  if (survey.companyId) return "CustomSurvey";
  if (survey.systemManaged !== undefined) return "StandardSurvey";
  return "StandardSurvey";
};

class SurveyShareStore {
  open = false;
  survey = null;
  mode = "internal";
  internalSearch = "";
  internalSuggestions = [];
  suggestionsLoading = false;
  selectedInternal = new Set();
  internalPage = 1;
  internalHasMore = true;
  internalError = "";
  inviteLoading = false;
  guestEmail = "";
  guestMessage = "";
  guestEmailError = "";
  inviteUrl = "";

  constructor() {
    makeAutoObservable(this);
  }

  get surveyType() {
    return resolveSurveyType(this.survey);
  }

  get selectedInternalCount() {
    return this.selectedInternal.size;
  }

  setSurvey = (survey) => {
    this.survey = survey || null;
  };
  setInviteUrl = (url) => {
    this.inviteUrl = url;
  };

  resetState = () => {
    this.mode = "internal";
    this.internalSearch = "";
    this.internalSuggestions = [];
    this.suggestionsLoading = false;
    this.selectedInternal = new Set();
    this.internalPage = 1;
    this.internalHasMore = true;
    this.internalError = "";
    this.inviteLoading = false;
    this.guestEmail = "";
    this.guestMessage = "";
    this.guestEmailError = "";
  };

  clear = () => {
    this.resetState();
    this.survey = null;
  };

  openDialog = async (survey) => {
    if (!survey) return;
    this.open = true;
    await this.initialize(survey);
  };

  closeDialog = () => {
    this.open = false;
    this.clear();
  };

  initialize = async (survey) => {
    this.setSurvey(survey);
    this.setInviteUrl("");
    this.resetState();
    await this.fetchSuggestions("", 1, true);
  };

  setMode = (value) => {
    this.mode = value;
  };

  setInternalSearch = (value) => {
    this.internalSearch = value;
  };

  setGuestEmail = (value) => {
    this.guestEmail = value;
    if (value.trim() && this.isInternalEmail(value)) {
      this.guestEmailError = t(
        "This email belongs to an internal user. Please use the Internal Users tab instead.",
      );
    } else {
      this.guestEmailError = "";
    }
  };

  setGuestMessage = (value) => {
    this.guestMessage = value;
  };

  isInternalEmail = (email) => {
    const normalizedEmail = email.trim().toLowerCase();
    return this.internalSuggestions.some(
      (user) => user.email?.toLowerCase() === normalizedEmail,
    );
  };

  toggleInternal = (email) => {
    if (!email) return;
    const next = new Set(this.selectedInternal);
    if (next.has(email)) {
      next.delete(email);
    } else {
      next.add(email);
    }
    this.selectedInternal = next;
    this.internalError = "";
  };

  fetchSuggestions = async (partial, page = 1, replace = false) => {
    if (!this.survey?._id) return;
    const trimmed = partial.trim();

    runInAction(() => {
      this.suggestionsLoading = true;
    });

    try {
      const data = await surveysService.fetchInviteSuggestions(
        this.surveyType,
        this.survey._id,
        trimmed,
        {
          showErrorToast: false,
        },
      );
      if (!data) throw new Error(t("Unable to load suggestions."));
      const payload = resolvePayload(data) || {};
      const next = payload?.suggestions || [];

      runInAction(() => {
        if (replace) {
          this.internalSuggestions = next;
        } else {
          const existing = new Set(
            this.internalSuggestions.map((item) => item.email),
          );
          const combined = [...this.internalSuggestions];
          next.forEach((item) => {
            if (!existing.has(item.email)) {
              combined.push(item);
            }
          });
          this.internalSuggestions = combined;
        }
        this.internalHasMore = next.length >= 5;
        this.internalPage = page;
      });
    } catch (error) {
      runInAction(() => {
        if (replace) this.internalSuggestions = [];
        this.internalHasMore = false;
      });
    } finally {
      runInAction(() => {
        this.suggestionsLoading = false;
      });
    }
  };

  searchInternal = async (value) => {
    this.setInternalSearch(value);
    this.internalPage = 1;
    this.internalHasMore = true;
    await this.fetchSuggestions(value, 1, true);
  };

  loadMoreInternal = async () => {
    if (!this.internalHasMore) return;
    await this.fetchSuggestions(this.internalSearch, this.internalPage + 1);
  };

  sendInternalInvites = async () => {
    if (!this.survey?._id) return false;
    const emails = Array.from(this.selectedInternal);
    if (!emails.length) {
      showToast.error(t("Select at least one internal user."));
      return false;
    }

    runInAction(() => {
      this.inviteLoading = true;
    });

    try {
      const data = await surveysService.inviteSurvey(
        this.surveyType,
        this.survey._id,
        {
          scope: "internal",
          allowExternalRespondents: false,
          inviteEmails: emails,
        },
      );
      if (!data) {
        throw new Error(t("Unable to send invites."));
      }
      showToast.success(
        t("Invited {{count}} internal users.", { count: emails.length }),
      );
      runInAction(() => {
        this.selectedInternal = new Set();
        this.internalError = "";
      });
      return true;
    } catch (error) {
      const message = error?.message || t("Unable to send invites.");
      runInAction(() => {
        this.internalError = message;
      });
      showToast.error(message);
      return false;
    } finally {
      runInAction(() => {
        this.inviteLoading = false;
      });
    }
  };

  sendGuestInvite = async () => {
    if (!this.survey?._id) return false;
    const email = this.guestEmail.trim().toLowerCase();
    if (!emailRegex.test(email)) {
      showToast.error(t("Enter a valid guest email."));
      return false;
    }
    if (this.isInternalEmail(email)) {
      showToast.error(
        t(
          "This email belongs to an internal user. Please use the Internal Users tab.",
        ),
      );
      return false;
    }

    runInAction(() => {
      this.inviteLoading = true;
    });

    try {
      const data = await surveysService.inviteSurvey(
        this.surveyType,
        this.survey._id,
        {
          scope: "public",
          allowExternalRespondents: true,
          inviteEmails: [email],
          message: this.guestMessage || undefined,
        },
      );
      if (!data) {
        throw new Error(t("Unable to send guest invite."));
      }
      const inviteUrl = data.deliveries?.[0]?.inviteUrl;
      showToast.success(t("Guest invite sent."));
      runInAction(() => {
        this.guestEmail = "";
        this.guestMessage = "";
        this.inviteUrl = inviteUrl;
      });
      return true;
    } catch (error) {
      showToast.error(error?.message || t("Unable to send guest invite."));
      return false;
    } finally {
      runInAction(() => {
        this.inviteLoading = false;
      });
    }
  };
}

export const surveyShareStore = new SurveyShareStore();
